import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
    Flex,
    View,
    DialogContainer,
    Dialog,
    Content,
    Header,
    Link,
} from "@adobe/react-spectrum";
import { FilterIcon } from "../../../../components/protected/Icons/IconsLib";
import { useTranslation } from "react-i18next";
import {
    IFilterControl,
    IFilterItem,
} from "../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/FilterSetupPlaceholder";
import { FilterControlRenderer } from "../../../../components/protected/Dialogs/components/FilterSetupPlaceholder/components/FilterControlRenderer";
import ActivityStatusFilterComponent from "../../../../components/protected/layout-components/FilterComponent/ActivityStatusFilter/ActivityStatusFilterComponent";
import ActivityTypeFilterComponent from "../../../../components/protected/layout-components/FilterComponent/ActivityTypeFilter/ActivityTypeFilterComponent";
import UsersFilterComponent from "../../../../components/protected/layout-components/FilterComponent/UsersFilter/UsersFilterComponent";
import { usePreloadAssets } from "../../../../hooks/UsePreloadAssets";
import {
    convertFilterControlToFilterParam,
    convertFilterParamToFilterItem,
    FilterParam,
} from "./filterUtils";
import styles from "./filter_component.module.css";
import { CustomView } from "../../../../services/soap/dataObjects/CustomView";
import { useDependency } from "../../../../contexts/DependencyProvider";
import QuickFilterControlsComponent from "./QuickFilterControlsComponent";
import { SettingItem } from "../../../../services/soap/features/responses/GetFormSettingsResponse";

// Types for parsed Custom View definition
interface CustomViewColumn {
    label: string;
    type: string;
    itemname: string;
    isVisible: boolean;
    isFilter: boolean;
}

interface CustomViewFilterInfo {
    name: string;
    type: string;
    value?: string;
    negation?: boolean;
    includeNull?: boolean;
    isCurrentUser?: boolean;
    valueLabel?: string;
}

interface CustomViewFilter {
    name: string;
    dataType: string;
    itemname: string;
    columnLabel: string;
    filterInfo?: CustomViewFilterInfo[];
}

interface ParsedCustomViewDefinition {
    columns?: CustomViewColumn[];
    filters?: CustomViewFilter[];
    sorting?: Array<{ column: string; itemname: string; level: string }>;
    viewAccess?: string;
}

export interface IFilterControlsComponentProps {
    filterControls?: IFilterControl[];
    filterParams: any;
    onFilterChange: (filterParams: {}) => void;
    createParams?: (filterTypeName: any, filterValue: any) => void;
    isMobile?: boolean;
    isOpen?: boolean;
    onClose?: () => void;
    onOpen?: () => void;
    showDefaultFilters?: boolean; // If true, show default filter components (ActivityStatus, Users, ActivityType)
    selectedView?: CustomView; // Currently selected Custom View for deriving filters
    fpaTypeCategory?: string;
    formSettings?: SettingItem[];
    resetBaselineRef?: React.MutableRefObject<(() => void) | null>;
}

const FilterControlsComponent: React.FC<IFilterControlsComponentProps> = ({
    filterControls = [],
    filterParams,
    onFilterChange,
    createParams,
    isMobile = false,
    isOpen = false,
    onClose,
    onOpen,
    showDefaultFilters = true,
    selectedView,
    fpaTypeCategory,
    formSettings,
    resetBaselineRef,
}) => {
    const { t } = useTranslation();
    const { store } = useDependency();
    const {
        folderTypes: folderTypesState,
        projectTypes: projectTypesState,
        folderStatusMapping2: folderStatusState2,
        folderStatusMapping: folderStatusState,
        projectStatusMapping: projectStatusState,
        activityStatusMapping: activityStatusState,
        activityTypes: activityTypesState,
    } = usePreloadAssets();

    // Internal state for filterControls to enable bidirectional sync
    const [internalFilterControls, setInternalFilterControls] =
        useState<IFilterControl[]>(filterControls);
    const internalFilterControlsRef = useRef<IFilterControl[]>(filterControls);
    const isUpdatingFromParams = useRef(false);
    const prevFilterParamsRef = useRef<any>(null);
    const justRestoredFromDefinitionRef = useRef<boolean>(false);

    // UI overrides state - stores user changes that override Custom View defaults
    const [uiOverrides, setUiOverrides] = useState<
        Record<string, IFilterItem[]>
    >({});
    const prevSelectedViewIdRef = useRef<string | undefined>(undefined);

    /**
     * Parses the Custom View definition JSON string into a typed object.
     */
    const parsedViewDefinition =
        useMemo((): ParsedCustomViewDefinition | null => {
            if (!selectedView?.definition) return null;
            try {
                return JSON.parse(
                    selectedView.definition
                ) as ParsedCustomViewDefinition;
            } catch {
                console.error("Failed to parse Custom View definition");
                return null;
            }
        }, [selectedView?.definition]);

    /**
     * Step 1: Filter Visibility
     *
     * Extracts columns marked as filterable (`isFilter === true`).
     * This determines WHICH filters appear in the UI.
     */
    const filterableColumns = useMemo((): CustomViewColumn[] => {
        if (!parsedViewDefinition?.columns) return [];
        return parsedViewDefinition.columns.filter(
            (col) => col.isFilter === true
        );
    }, [parsedViewDefinition?.columns]);

    /**
     * Step 2: Default Value Resolution
     *
     * For each filterable column, resolve its default value from the `filters[]` section.
     * Matches by `itemname`.
     *
     * Resolution priority:
     * 1. UI overrides (user changes) - highest priority
     * 2. Default values from `filters[].filterInfo[]`
     * 3. Empty default state `[{ name: 'value', type: 'equals', value: '' }]`
     */
    const viewDerivedFilters = useMemo((): IFilterControl[] => {
        if (filterableColumns.length === 0) return [];

        return filterableColumns.map((column): IFilterControl => {
            // Look up default values from the filters section by itemname
            const matchingFilter = parsedViewDefinition?.filters?.find(
                (f) => f.itemname === column.itemname
            );

            // Convert filterInfo to IFilterItem[], or use empty default
            const defaultFilterInfo: IFilterItem[] =
                matchingFilter?.filterInfo?.map((fi) => {
                    return {
                        name: fi.name,
                        type: fi.type,
                        value: fi.value,
                        negation: fi.negation,
                        includeNull: fi.includeNull,
                        // Preserve isCurrentUser and valueLabel if present in definition
                        ...(fi.isCurrentUser !== undefined && {
                            isCurrentUser: fi.isCurrentUser,
                        }),
                        ...(fi.valueLabel !== undefined && {
                            valueLabel: fi.valueLabel,
                        }),
                    };
                }) || [{ name: "value", type: "contains", value: "" }];

            // UI overrides take precedence over defaults
            const overrideKey = column.itemname;
            const effectiveFilterInfo =
                uiOverrides[overrideKey] || defaultFilterInfo;

            // Find matching formSetting to get the id for IColumnItem
            const matchingFormSetting = formSettings?.find(
                (item) => item.itemname === column.itemname
            );

            return {
                name: column.itemname,
                label: column.label || column.itemname || "",
                dataType: column.type,
                data: {
                    id: matchingFormSetting
                        ? String(matchingFormSetting.id)
                        : "",
                    label: column.label,
                    type: column.type,
                    itemname: column.itemname,
                    isVisible: column.isVisible,
                },
                filterInfo: effectiveFilterInfo,
            };
        });
    }, [
        filterableColumns,
        parsedViewDefinition?.filters,
        uiOverrides,
        formSettings,
    ]);

    // Reset UI overrides when selected view changes
    useEffect(() => {
        if (selectedView?.id !== prevSelectedViewIdRef.current) {
            setUiOverrides({});
            prevSelectedViewIdRef.current = selectedView?.id;
        }
    }, [selectedView?.id]);

    // Determine effective filter controls: view-derived takes precedence over prop-based
    const effectiveFilterControls = useMemo(() => {
        if (viewDerivedFilters.length > 0) {
            return viewDerivedFilters;
        }
        return internalFilterControls;
    }, [viewDerivedFilters, internalFilterControls]);

    const mapStatusRecordToOptions = useCallback(
        (record: Record<string, any> | undefined): any[] => {
            if (!record) return [];
            return Object.entries(record).map(([id, value]) => ({
                id: String(id),
                label: value?.name ?? value?.status?.name ?? String(id),
                color:
                    value?.status?.color ??
                    value?.color ??
                    value?.STATUS?.color ??
                    "#9E9E9E",
            }));
        },
        []
    );

    const mapTypeRecordToOptions = useCallback(
        (record: Record<string, any> | any[] | undefined): any[] => {
            if (!record) return [];
            const values = Array.isArray(record)
                ? record
                : Object.values(record);

            return values
                .map((value) => {
                    const rawId =
                        value?.id ??
                        value?.TYPE?.id ??
                        value?.typeId ??
                        value?.TYPE_ID ??
                        value?.TypeId;
                    const rawLabel =
                        value?.name ??
                        value?.TYPE?.name ??
                        value?.typeName ??
                        value?.TYPE_NAME ??
                        value?.TypeName ??
                        (rawId !== undefined ? String(rawId) : undefined);

                    if (rawId === undefined && !rawLabel) {
                        return null;
                    }

                    return {
                        id: String(rawId ?? rawLabel),
                        label: rawLabel ? String(rawLabel) : String(rawId),
                    };
                })
                .filter((option): option is any => Boolean(option));
        },
        []
    );

    const folderStatusOptions = mapStatusRecordToOptions(
        folderStatusState2 || folderStatusState
    );
    const projectStatusOptions = mapStatusRecordToOptions(projectStatusState);
    const activityStatusOptions = mapStatusRecordToOptions(activityStatusState);

    const folderTypeOptions = mapTypeRecordToOptions(folderTypesState);
    const projectTypeOptions = mapTypeRecordToOptions(projectTypesState);
    const activityTypeOptions = mapTypeRecordToOptions(activityTypesState);

    // Sync internalFilterControls when filterControls prop changes
    useEffect(() => {
        setInternalFilterControls(filterControls);
        internalFilterControlsRef.current = filterControls;

        // When filterControls change (e.g., custom view selected), sync with filterParams
        // This ensures filter panel UI shows the correct values from filterParams
        if (
            filterControls.length > 0 &&
            filterParams &&
            !isUpdatingFromParams.current
        ) {
            const currentFilterCol = filterParams?.FILTER?.COL || [];

            // Update filterControls to match filterParams values
            const syncedControls = filterControls.map((control) => {
                const matchingFilterParam = currentFilterCol.find(
                    (col: FilterParam) => col.name === control.name
                );

                if (matchingFilterParam) {
                    const filterItem =
                        convertFilterParamToFilterItem(matchingFilterParam);
                    if (filterItem) {
                        // Preserve valueLabel and isCurrentUser from original filterInfo if present
                        // FilterParams don't have these properties, so we need to preserve them from viewDerivedFilters
                        const originalFilterInfo = control.filterInfo?.[0];
                        const preservedFilterInfo = {
                            name: "value",
                            type: filterItem.type,
                            value: filterItem.value,
                            negation: filterItem.negation,
                            includeNull: filterItem.includeNull,
                            // Preserve valueLabel if it exists in original (from Custom View definition)
                            ...(originalFilterInfo?.valueLabel !== undefined && {
                                valueLabel: originalFilterInfo.valueLabel,
                            }),
                            // Preserve isCurrentUser if it exists in original (from Custom View definition)
                            ...(originalFilterInfo?.isCurrentUser !== undefined && {
                                isCurrentUser: originalFilterInfo.isCurrentUser,
                            }),
                        };

                        return {
                            ...control,
                            filterInfo: [preservedFilterInfo],
                        };
                    }
                }

                // If control has filterInfo with values but no matching param, keep the control as is
                // This handles the case where filterInfo is already populated from custom view definition
                return control;
            });

            // Only update if there are actual differences to avoid unnecessary re-renders
            const hasChanges = syncedControls.some((synced, index) => {
                const original = filterControls[index];
                if (!original) return false;

                const syncedValue = synced.filterInfo?.[0]?.value || "";
                const originalValue = original.filterInfo?.[0]?.value || "";
                const syncedType = synced.filterInfo?.[0]?.type || "";
                const originalType = original.filterInfo?.[0]?.type || "";

                return (
                    syncedValue !== originalValue || syncedType !== originalType
                );
            });

            if (hasChanges) {
                isUpdatingFromParams.current = true;
                setInternalFilterControls(syncedControls);
                internalFilterControlsRef.current = syncedControls;

                setTimeout(() => {
                    isUpdatingFromParams.current = false;
                }, 0);
            }
        }
    }, [filterControls, filterParams]);

    // Sync filterControls from filterParams (bidirectional sync)
    useEffect(() => {
        // Skip if we're currently updating from filterParams to avoid loops
        if (isUpdatingFromParams.current) {
            return;
        }

        // Skip if we just restored from Custom View definition to preserve valueLabel and other properties
        if (justRestoredFromDefinitionRef.current) {
            // Reset the flag after skipping once
            justRestoredFromDefinitionRef.current = false;
            // Update prevFilterParamsRef to match current filterParams so we don't skip unnecessarily next time
            prevFilterParamsRef.current = filterParams;
            return;
        }

        // Skip if filterParams hasn't actually changed
        if (prevFilterParamsRef.current === filterParams) {
            return;
        }

        // Get current filterControls from ref to avoid dependency issues
        const currentFilterControls = internalFilterControlsRef.current;

        // Skip if no filterControls to sync
        if (currentFilterControls.length === 0) {
            prevFilterParamsRef.current = filterParams;
            return;
        }

        // Check if filterParams actually changed in a meaningful way
        const currentFilterCol = filterParams?.FILTER?.COL || [];
        const prevFilterCol = prevFilterParamsRef.current?.FILTER?.COL || [];

        // Compare filterParams.FILTER.COL arrays using deep comparison
        const currentFilterColStr = JSON.stringify(currentFilterCol);
        const prevFilterColStr = JSON.stringify(prevFilterCol);
        const hasChanged = currentFilterColStr !== prevFilterColStr;

        if (!hasChanged) {
            // FILTER.COL hasn't changed, so filterParams hasn't changed meaningfully
            // Update prevFilterParamsRef to match current filterParams reference
            prevFilterParamsRef.current = filterParams;
            return;
        }

        // Update filterControls based on filterParams
        const updatedControls = currentFilterControls.map((control) => {
            const expectedFilterName = control.name;
            const matchingFilterParam = currentFilterCol.find(
                (col: FilterParam) => col.name === expectedFilterName
            );

            if (matchingFilterParam) {
                const filterItem =
                    convertFilterParamToFilterItem(matchingFilterParam);
                if (filterItem) {
                    // Preserve valueLabel and isCurrentUser from current control if present
                    // FilterParams don't have these properties, so we need to preserve them
                    const currentFilterInfo = control.filterInfo?.[0];
                    const preservedFilterInfo: IFilterItem = {
                        name: "value",
                        type: filterItem.type,
                        value: filterItem.value,
                        negation: filterItem.negation,
                        includeNull: filterItem.includeNull,
                        // Preserve valueLabel if it exists in current control (from Custom View definition)
                        ...(currentFilterInfo?.valueLabel !== undefined && {
                            valueLabel: currentFilterInfo.valueLabel,
                        }),
                        // Preserve isCurrentUser if it exists in current control (from Custom View definition)
                        ...(currentFilterInfo?.isCurrentUser !== undefined && {
                            isCurrentUser: currentFilterInfo.isCurrentUser,
                        }),
                    };

                    // Derive valueLabel for date presets if missing
                    if (!preservedFilterInfo.valueLabel && filterItem.type === "relative_preset" && filterItem.value) {
                        const presetLabelMap: Record<string, string> = {
                            today: "Today",
                            yesterday: "Yesterday",
                            tomorrow: "Tomorrow",
                            last_7_days: "Last 7 Days",
                            next_7_days: "Next 7 Days",
                            last_30_days: "Last 30 Days",
                            next_30_days: "Next 30 Days",
                            last_month: "Last Month",
                            this_month: "This Month",
                            next_month: "Next Month",
                            last_quarter: "Last Quarter",
                            this_quarter: "This Quarter",
                            next_quarter: "Next Quarter",
                            last_year: "Last Year",
                            this_year: "This Year",
                            next_year: "Next Year",
                        };
                        preservedFilterInfo.valueLabel = presetLabelMap[filterItem.value] || filterItem.value;
                    }

                    return {
                        ...control,
                        filterInfo: [preservedFilterInfo],
                    };
                }
            } else {
                // If filterParam doesn't exist, clear the filterInfo
                // Determine default type based on control's dataType
                // Status and type filters use 'in', others use 'contains'
                const getDefaultType = (dataType: string): string => {
                    if (
                        dataType === 'activity_status' ||
                        dataType === 'folder_status' ||
                        dataType === 'project_status' ||
                        dataType === 'activity_type' ||
                        dataType === 'folder_type' ||
                        dataType === 'project_type'
                    ) {
                        return 'in';
                    }
                    return 'contains';
                };

                return {
                    ...control,
                    filterInfo: [
                        {
                            name: "value",
                            type: getDefaultType(control.dataType),
                            value: "",
                        },
                    ],
                };
            }

            return control;
        });

        isUpdatingFromParams.current = true;
        setInternalFilterControls(updatedControls);
        internalFilterControlsRef.current = updatedControls;
        prevFilterParamsRef.current = filterParams;

        // Reset flag after state update
        setTimeout(() => {
            isUpdatingFromParams.current = false;
        }, 0);
    }, [filterParams]);

    const handleFilterChange = useCallback(
        (updatedFilter: IFilterControl) => {
            // If using view-derived filters, update UI overrides instead of internal state
            if (viewDerivedFilters.length > 0) {
                setUiOverrides((prev) => ({
                    ...prev,
                    [updatedFilter.data.itemname]: updatedFilter.filterInfo,
                }));
            } else {
                // Update internal state for prop-based filters
                const updatedControls = internalFilterControls.map((control) =>
                    control.data.itemname === updatedFilter.data.itemname
                        ? updatedFilter
                        : control
                );
                setInternalFilterControls(updatedControls);
                internalFilterControlsRef.current = updatedControls;
            }

            // Convert IFilterControl to FilterParam and update filterParams
            const filterParam = convertFilterControlToFilterParam(
                updatedFilter,
                store
            );

            if (!filterParam) {
                return;
            }

            // Update filterParams.FILTER.COL array
            const updatedFilterParams = { ...filterParams };
            if (!updatedFilterParams.FILTER) {
                updatedFilterParams.FILTER = { COL: [] };
            }
            if (!updatedFilterParams.FILTER.COL) {
                updatedFilterParams.FILTER.COL = [];
            }

            // Find existing entry with same name
            const existingIndex = updatedFilterParams.FILTER.COL.findIndex(
                (col: FilterParam) => col.name === filterParam.name
            );

            // Allow empty values for 'empty' type filters (is_empty/is_not_empty)
            const isEmptyType = filterParam.type === "empty";
            const isEmptyValue =
                filterParam.value === "" ||
                filterParam.value === null ||
                filterParam.value === undefined;

            if (!isEmptyType && isEmptyValue) {
                // Remove entry if value is empty (but preserve 'empty' type filters)
                updatedFilterParams.FILTER.COL =
                    updatedFilterParams.FILTER.COL.filter(
                        (col: FilterParam) => col.name !== filterParam.name
                    );
            } else if (existingIndex !== -1) {
                // Update existing entry
                updatedFilterParams.FILTER.COL[existingIndex] = filterParam;
            } else {
                // Add new entry
                updatedFilterParams.FILTER.COL.push(filterParam);
            }

            // Remove empty values (but preserve 'empty' type filters which have empty values)
            updatedFilterParams.FILTER.COL =
                updatedFilterParams.FILTER.COL.filter(
                    (col: FilterParam) =>
                        col.type === "empty" ||
                        (col.value !== "" &&
                            col.value !== null &&
                            col.value !== undefined)
                );

            // Update prevFilterParamsRef to prevent sync loop
            prevFilterParamsRef.current = updatedFilterParams;

            // Call onFilterChange to notify parent
            onFilterChange(updatedFilterParams);
        },
        [
            filterParams,
            onFilterChange,
            internalFilterControls,
            viewDerivedFilters.length,
            store,
        ]
    );

    const handleResetFilters = useCallback(() => {
        // Check if a custom view is selected (has 'state' filter)
        const stateFilter = filterParams?.FILTER?.COL?.find(
            (col: FilterParam) => col.name === "state"
        );
        const selectedViewId = stateFilter?.value;

        if (selectedViewId && selectedView?.definition) {
            // If a custom view is selected, reset to its default filter values
            try {
                const definition = JSON.parse(selectedView.definition);
                const updatedParams: any = {
                    FILTER: { COL: [] },
                };

                // Extract FIELDS from columns
                if (definition.columns && Array.isArray(definition.columns)) {
                    const fields = definition.columns
                        .filter((col: any) => col.isVisible !== false)
                        .map((col: any) => col.itemname)
                        .filter(
                            (itemname: any) =>
                                itemname != null && itemname !== ""
                        );

                    if (fields.length > 0) {
                        updatedParams.FIELDS = { field: fields };
                    }
                }

                // Extract ORDER from sorting
                if (
                    definition.sorting &&
                    Array.isArray(definition.sorting) &&
                    definition.sorting.length > 0
                ) {
                    const order = {
                        COL: definition.sorting.map((sort: any) => {
                            let sortName = sort.itemname;
                            if (sortName && sortName.includes("|")) {
                                const parts = sortName.split("|");
                                if (
                                    parts.length > 1 &&
                                    parts[1]?.trim() !== ""
                                ) {
                                    sortName = parts[1].trim();
                                }
                            }
                            return {
                                name: sortName,
                                desc: sort.level === "Descending" ? "1" : "0",
                            };
                        }),
                    };
                    updatedParams.ORDER = order;
                }

                // Apply preset filter values from filters[] to filterParams.FILTER.COL
                if (Array.isArray(definition.filters)) {
                    definition.filters.forEach((filterDef: any) => {
                        if (!filterDef?.filterInfo?.length) return;

                        const filterInfo = filterDef.filterInfo[0];
                        const isEmptyOperator =
                            filterInfo?.type === "is_empty" ||
                            filterInfo?.type === "is_not_empty";
                        if (!isEmptyOperator && !filterInfo?.value) return;

                        let filterName = filterDef.name;
                        if (filterName && filterName.includes("|")) {
                            const parts = filterName.split("|");
                            filterName = parts[0]?.trim() || filterName;
                        }

                        let filterType = filterInfo.type || "equals";
                        let negation = false;

                        if (filterInfo.type === "is_empty") {
                            filterType = "empty";
                            negation = false;
                        } else if (filterInfo.type === "is_not_empty") {
                            filterType = "empty";
                            negation = true;
                        }

                        let filterValue = filterInfo.value || "";
                        if (filterInfo.isCurrentUser === true && store) {
                            filterValue = String(store.UserId);
                        }

                        const filterParam: any = {
                            name: filterName,
                            type: filterType,
                            value: filterValue,
                        };

                        if (negation) {
                            filterParam.negation = true;
                        }

                        updatedParams.FILTER.COL.push(filterParam);
                    });
                }

                // Add state filter to preserve view selection
                updatedParams.FILTER.COL.push({
                    name: "state",
                    type: "equals",
                    value: selectedViewId,
                });

                // Clear UI overrides to reset to default values
                // This is important when using viewDerivedFilters
                setUiOverrides({});

                // Build lookup of filters from Custom View definition by itemname
                // This allows us to restore the original filterInfo with all properties (valueLabel, isCurrentUser, etc.)
                const definitionFiltersByItemname: Record<string, any> = {};
                if (Array.isArray(definition.filters)) {
                    definition.filters.forEach((f: any) => {
                        // Match by itemname (preferred) or by name (fallback)
                        if (f.itemname) {
                            definitionFiltersByItemname[f.itemname] = f;
                        }
                        // Also index by name (handle pipe character - use part before pipe)
                        if (f.name) {
                            let filterName = f.name;
                            if (filterName.includes("|")) {
                                const parts = filterName.split("|");
                                filterName = parts[0]?.trim() || filterName;
                            }
                            // Only add if itemname wasn't already set
                            if (!f.itemname && !definitionFiltersByItemname[filterName]) {
                                definitionFiltersByItemname[filterName] = f;
                            }
                        }
                    });
                }

                // Update internalFilterControls to match reset values
                // This ensures the filter panel UI reflects the reset state
                const controlsToUpdate =
                    viewDerivedFilters.length > 0
                        ? viewDerivedFilters
                        : internalFilterControls;

                if (controlsToUpdate.length > 0) {
                    const resetControls = controlsToUpdate.map((control) => {
                        // Try to find matching filter in Custom View definition by itemname
                        const matchingDefinitionFilter =
                            definitionFiltersByItemname[control.data.itemname] ||
                            definitionFiltersByItemname[control.name];

                        if (
                            matchingDefinitionFilter?.filterInfo &&
                            Array.isArray(matchingDefinitionFilter.filterInfo) &&
                            matchingDefinitionFilter.filterInfo.length > 0
                        ) {
                            // Use original filterInfo directly from definition (deep copy)
                            // This preserves all properties: value, valueLabel, isCurrentUser, type, negation, includeNull, etc.
                            const restoredFilterInfo = JSON.parse(
                                JSON.stringify(
                                    matchingDefinitionFilter.filterInfo
                                )
                            );
                            return {
                                ...control,
                                filterInfo: restoredFilterInfo,
                            };
                        }

                        // If no matching filter in definition, reset to empty
                        // Determine default type based on control's dataType
                        // Status and type filters use 'in', others use 'contains'
                        const getDefaultType = (dataType: string): string => {
                            if (
                                dataType === 'activity_status' ||
                                dataType === 'folder_status' ||
                                dataType === 'project_status' ||
                                dataType === 'activity_type' ||
                                dataType === 'folder_type' ||
                                dataType === 'project_type'
                            ) {
                                return 'in';
                            }
                            return 'contains';
                        };

                        return {
                            ...control,
                            filterInfo: [
                                {
                                    name: "value",
                                    type: getDefaultType(control.dataType),
                                    value: "",
                                },
                            ],
                        };
                    });

                    isUpdatingFromParams.current = true;

                    // Update the appropriate state based on which controls are being used
                    if (viewDerivedFilters.length > 0) {
                        // For viewDerivedFilters, clearing uiOverrides is enough
                        // The viewDerivedFilters will automatically use defaults from parsedViewDefinition
                    } else {
                        // For internalFilterControls, update them directly
                        // Use functional update to ensure state change is detected
                        setInternalFilterControls(() => resetControls);
                        internalFilterControlsRef.current = resetControls;
                        // Mark that we just restored from definition - this prevents sync useEffect from overwriting
                        justRestoredFromDefinitionRef.current = true;
                    }
                }

                // Update prevFilterParamsRef to prevent sync loop
                // IMPORTANT: Use deep copy to ensure reference equality check works correctly
                // Set this BEFORE calling onFilterChange so sync useEffect sees params as "already processed"
                prevFilterParamsRef.current = JSON.parse(JSON.stringify(updatedParams));
                
                // Keep isUpdatingFromParams flag set during reset to prevent sync useEffect from overwriting
                // We'll reset it after child components have time to react to restored filterInfo
                onFilterChange(updatedParams);
                
                // Reset baseline after filters are reset (with delay to ensure filterParams is updated)
                if (resetBaselineRef?.current) {
                    setTimeout(() => {
                        resetBaselineRef.current?.();
                    }, 100);
                }
                
                // Reset flag after delay to allow sync useEffect to skip
                // Use longer delay to ensure child components have time to react to restored filterInfo
                setTimeout(() => {
                    isUpdatingFromParams.current = false;
                }, 300);
            } catch (error) {
                console.error(
                    "Error resetting filters for custom view:",
                    error
                );
                // Fallback to default reset
                const defaultParams = {
                    FILTER: { COL: [] },
                    FIELDS: {
                        field: [
                            "customStateName",
                            "created",
                            "folderName",
                            "projectName",
                            "name",
                            "refId",
                        ],
                    },
                };
                onFilterChange(defaultParams);
                
                // Reset baseline after filters are reset (with delay to ensure filterParams is updated)
                if (resetBaselineRef?.current) {
                    setTimeout(() => {
                        resetBaselineRef.current?.();
                    }, 100);
                }
            }
        } else {
            // No custom view selected, reset to default state
            const defaultParams = {
                FILTER: { COL: [] },
                FIELDS: {
                    field: [
                        "customStateName",
                        "created",
                        "folderName",
                        "projectName",
                        "name",
                        "refId",
                    ],
                },
            };
            //delete defaultParams.ORDER;
            onFilterChange(defaultParams);
        }
    }, [
        filterParams,
        selectedView,
        store,
        onFilterChange,
        viewDerivedFilters,
        internalFilterControls,
        resetBaselineRef,
    ]);

    const renderFilterControls = () => {
        // If filterControls are provided (from view or props), render them dynamically
        if (effectiveFilterControls.length > 0) {
            return (
                <Flex
                    direction={"column"}
                    width={"100%"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"12px"}
                    UNSAFE_className={styles.popup_header_body}
                >
                    {effectiveFilterControls.map((filter) => (
                        <View key={filter.data.itemname} width={"100%"}>
                            <FilterControlRenderer
                                filter={filter}
                                onFilterChange={handleFilterChange}
                                statusOptions={{
                                    folder: folderStatusOptions,
                                    project: projectStatusOptions,
                                    activity: activityStatusOptions,
                                }}
                                typeOptions={{
                                    folder: folderTypeOptions,
                                    project: projectTypeOptions,
                                    activity: activityTypeOptions,
                                }}
                                fpaTypeCategory={fpaTypeCategory}
                            />
                        </View>
                    ))}
                </Flex>
            );
        }

        // Otherwise, render default filter components if showDefaultFilters is true
        if (showDefaultFilters && createParams) {
            return (
                <Flex
                    direction={"column"}
                    width={"100%"}
                    alignItems={"start"}
                    justifyContent={"start"}
                    gap={"12px"}
                    UNSAFE_className={styles.popup_header_body}
                >
                    <ActivityStatusFilterComponent
                        createParams={createParams}
                        filterParams={filterParams}
                    />
                    <UsersFilterComponent
                        createParams={createParams}
                        filterParams={filterParams}
                    />
                    <ActivityTypeFilterComponent
                        createParams={createParams}
                        filterParams={filterParams}
                    />
                </Flex>
            );
        }

        return null;
    };

    if (isMobile) {
        return (
            <>
                <View
                    position={"absolute"}
                    top={"calc(50% - 12px)"}
                    left={"calc(100% - 40px)"}
                    width={"24px"}
                    height={"24px"}
                    overflow={"hidden"}
                >
                    <div onClick={onOpen} style={{ cursor: "pointer" }}>
                        <FilterIcon size={20} />
                    </div>
                </View>

                <DialogContainer
                    isDismissable
                    onDismiss={onClose || (() => {})}
                >
                    {isOpen && (
                        <Dialog size="L">
                            <Content>
                                <Header>
                                    <Flex
                                        position={"relative"}
                                        direction={"row"}
                                        justifyContent={"space-between"}
                                        alignContent={"center"}
                                        width={"100%"}
                                        alignItems={"center"}
                                    >
                                        <View
                                            position={"relative"}
                                            UNSAFE_className={
                                                styles.popup_header_text
                                            }
                                        >
                                            {t("filters", {
                                                ns: "layout_components",
                                            })}
                                        </View>
                                        <Flex
                                            direction={"row"}
                                            gap={"size-100"}
                                            alignItems={"center"}
                                        >
                                            <Link
                                                isQuiet
                                                onPress={handleResetFilters}
                                            >
                                                {t("reset_filters", {
                                                    ns: "layout_components",
                                                    defaultValue:
                                                        "Reset Filters",
                                                })}
                                            </Link>
                                        </Flex>
                                    </Flex>
                                </Header>
                                <View
                                    width={"100%"}
                                    position={"relative"}
                                    height={"100vh"}
                                >
                                    <Flex
                                        direction={"column"}
                                        position={"absolute"}
                                        width={"95%"}
                                        top={"30px"}
                                        left={"16px"}
                                        alignItems={"start"}
                                        justifyContent={"start"}
                                        gap={"24px"}
                                        UNSAFE_className={
                                            styles.popup_header_body_parent
                                        }
                                    >
                                        {renderFilterControls()}
                                        {formSettings && (
                                            <QuickFilterControlsComponent
                                                formSettings={formSettings}
                                                filterParams={filterParams}
                                                onFilterChange={onFilterChange}
                                                fpaTypeCategory={
                                                    fpaTypeCategory
                                                }
                                            />
                                        )}
                                    </Flex>
                                </View>
                            </Content>
                        </Dialog>
                    )}
                </DialogContainer>
            </>
        );
    }

    // Desktop layout
    return (
        <Flex
            direction={"column"}
            width={"100%"}
            alignItems={"start"}
            justifyContent={"start"}
            gap={"5px"}
            UNSAFE_className={styles.filters_menu_container}
        >
            <Flex
                direction={"row"}
                alignSelf={"stretch"}
                alignItems={"center"}
                justifyContent={"space-between"}
                UNSAFE_className={styles.filters_menu_wrapper}
            >
                <View
                    position={"relative"}
                    UNSAFE_className={styles.menu_heading_text}
                >
                    {t("filters", { ns: "layout_components" })}
                </View>
                <Flex direction={"row"} gap={"size-100"} alignItems={"center"}>
                    <Link isQuiet onPress={handleResetFilters}>
                        {t("reset_filters", {
                            ns: "layout_components",
                            defaultValue: "Reset Filters",
                        })}
                    </Link>
                </Flex>
            </Flex>
            {effectiveFilterControls.length > 0 ? (
                effectiveFilterControls.map((filter) => (
                    <View key={filter.data.itemname} width={"100%"}>
                        <FilterControlRenderer
                            filter={filter}
                            onFilterChange={handleFilterChange}
                            statusOptions={{
                                folder: folderStatusOptions,
                                project: projectStatusOptions,
                                activity: activityStatusOptions,
                            }}
                            typeOptions={{
                                folder: folderTypeOptions,
                                project: projectTypeOptions,
                                activity: activityTypeOptions,
                            }}
                            fpaTypeCategory={fpaTypeCategory}
                        />
                    </View>
                ))
            ) : showDefaultFilters && createParams ? (
                <>
                    <ActivityStatusFilterComponent
                        createParams={createParams}
                        filterParams={filterParams}
                    />
                    <UsersFilterComponent
                        createParams={createParams}
                        filterParams={filterParams}
                    />
                    <ActivityTypeFilterComponent
                        createParams={createParams}
                        filterParams={filterParams}
                    />
                </>
            ) : null}
        </Flex>
    );
};

export default FilterControlsComponent;
